import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Building2,
  Smartphone,
  Wallet,
  QrCode,
  Banknote,
  Send,
} from "lucide-react";

const METHODS = [
  {
    icon: Smartphone,
    title: "Mobile money",
    body: "M-Pesa, MTN MoMo, Orange Money, Airtel Money — instant wallet credit.",
    tag: "34 markets",
    span: "md:col-span-6 md:row-span-2",
    accent: true,
  },
  {
    icon: Building2,
    title: "Bank transfer",
    body: "GTBank, Zenith, Equity, Access, KCB — real-time via NIP, EFT & RTGS.",
    tag: "500+ banks",
    span: "md:col-span-6",
  },
  {
    icon: QrCode,
    title: "Scan-to-pay",
    body: "EMVCo QR at merchants — Bancontact-style universal payment handle.",
    tag: "@your.handle",
    span: "md:col-span-3",
  },
  {
    icon: Banknote,
    title: "Cash pickup",
    body: "35,000+ agent locations across West, East and Southern Africa.",
    tag: "24/7",
    span: "md:col-span-3",
  },
  {
    icon: Wallet,
    title: "Cash apps",
    body: "Push to Chipper, Kuda, PalmPay, Wave and more — one hop, zero friction.",
    tag: "12 apps",
    span: "md:col-span-4",
  },
  {
    icon: Send,
    title: "Airtime & bills",
    body: "Send airtime, pay school fees or DSTV in seconds without leaving the chat.",
    tag: "coming soon",
    span: "md:col-span-8",
  },
];

export default function PayoutMethods() {
  const { t } = useTranslation();
  return (
    <section className="py-24 md:py-32 bg-surface relative" data-testid="payout-methods">
      <div className="container-x">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-12 md:mb-16">
          <div className="md:col-span-8">
            <div className="label-eyebrow">{t("home.payout.eyebrow")}</div>
            <h2 className="mt-4 font-display font-medium text-forest text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
              {t("home.payout.title")} <span className="italic font-serif text-forest/80">{t("home.payout.italic")}</span>
            </h2>
          </div>
          <div className="md:col-span-4 text-ink/70">
            Six delivery rails, one price. Choose per recipient — Instantxfer CashApp figures out the fastest
            route to their pocket.
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-12 md:auto-rows-[180px] gap-4 md:gap-6">
          {METHODS.map((m, i) => (
            <Card key={m.title} m={m} index={i} className={m.span} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Card({ m, index, className }) {
  const Icon = m.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1], delay: (index % 4) * 0.06 }}
      className={`col-span-2 ${className} card-flat p-6 md:p-8 relative overflow-hidden flex flex-col justify-between ${
        m.accent ? "!bg-forest !border-forest/50 text-canvas" : ""
      }`}
      data-testid={`payout-${m.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <div className="flex items-start justify-between">
        <div
          className={`w-11 h-11 rounded-full grid place-items-center border ${
            m.accent
              ? "bg-lime text-ink border-transparent"
              : "bg-canvas text-forest border-forest/15"
          }`}
        >
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <span
          className={`text-[10px] tracking-[0.22em] uppercase font-semibold ${
            m.accent ? "text-lime" : "text-forest/70"
          }`}
        >
          {m.tag}
        </span>
      </div>
      <div>
        <h3 className={`font-display font-medium tracking-tight text-2xl md:text-3xl mt-6 ${m.accent ? "text-canvas" : "text-forest"}`}>
          {m.title}
        </h3>
        <p className={`mt-3 text-sm md:text-base leading-relaxed ${m.accent ? "text-canvas/75" : "text-ink/70"}`}>
          {m.body}
        </p>
      </div>
      {m.accent && (
        <div className="absolute right-0 bottom-0 pointer-events-none opacity-25">
          <div className="text-[10rem] leading-none font-display text-lime -mr-6 -mb-8">↝</div>
        </div>
      )}
    </motion.div>
  );
}
